import { customAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const apiUrl = (environment: string) =>
  environment === "development"
    ? "https://sum-qa02.shareworks.com"
    : "https://shareworks-api.solium.com";

export const shareworks = defineConnector({
  identifier: "shareworks",
  name: "Shareworks",
  tagLine: "Equity compensation management",
  logo,
  tags: ["data"],
  protocol: "http",
  defaultUrlPattern: ({ environment }) =>
    `^${exactUrlToRegex(`${apiUrl(environment)}/rest`)}$`,
  auth: customAuth("shareworks"),
  steps: [
    {
      title: "Prepare Shareworks API access",
      items: [
        {
          kind: "markdown",
          content:
            "Ask Shareworks to allow the IP addresses your 3B stack uses and to create an API administrator account. In API Management, register your RSA public key, then copy the client ID and API secret key. Shareworks provides separate development and production environments.",
        },
        {
          kind: "select",
          label: "Environment",
          target: { kind: "configuration", field: "environment" },
          options: [
            { label: "Production", value: "production" },
            { label: "Development", value: "development" },
          ],
          defaultValue: "production",
          required: true,
        },
      ],
    },
    {
      title: "Enter your Shareworks credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          target: { kind: "configuration", field: "clientId" },
          required: true,
        },
        {
          kind: "input",
          label: "Account number",
          target: { kind: "configuration", field: "accountNumber" },
          required: true,
          helperText: "Used as the subject of Shareworks’ verification JWT.",
        },
        {
          kind: "input",
          label: "JWT audience",
          target: { kind: "configuration", field: "audience" },
          defaultValue: "shareworks-api.solium.com",
          required: true,
          helperText:
            "Use the audience Shareworks assigned to your API environment.",
        },
        {
          kind: "input",
          label: "API secret key",
          target: { kind: "secrets", field: "apiSecret" },
          required: true,
        },
        {
          kind: "file",
          label: "RSA private key",
          accept: ".pem,.key",
          target: { kind: "secrets", field: "privateKey" },
          required: true,
          helperText:
            "Required. Upload the unencrypted PEM private key that matches the public key registered with Shareworks.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ environment }) => ({
      url: `${apiUrl(environment)}/rest/admin/v1/auth/tokens`,
      method: "POST",
      headers: { "Content-Type": "text/plain", "Content-Length": "0" },
      body: "",
    }),
  },
  usageHint: ({ environment }) =>
    `Use Shareworks’ REST API at ${apiUrl(environment)}/rest/. The connector exchanges a signed verification JWT for an access token automatically.`,
});
